<template>
  <div
    class="right-fixed-info"
    :class="{ fixed: isFixed }"
    :style="{ top: calcFixedTop }"
  >
    <CouponDetail
      v-if="coupon"
      :disabled="!isCouponAvailable"
      class="coupon-detail"
    />
    <div class="summary-info">
      <div class="summary-info-title">
        Summary
      </div>
      <div class="summary-item">
        <div class="text">
          Total items
        </div>
        <div class="count">
          <template v-if="isEmpty">
            --
          </template>
          <template v-else>
            <span>{{ currencyData?.targetCurrency.slice(0, 2) }}&nbsp;</span>
            <span>{{ currencyData?.targetUnit }}</span>
            <span>{{ isLoading ? '0.00' : getPrice.totalItems }}</span>
          </template>
        </div>
      </div>
      <div
        class="summary-item"
        v-if="isCouponAvailable"
      >
        <div class="text">
          Discount
        </div>
        <div class="count">
          -US ${{ coupon.discountAmount }}
        </div>
      </div>
      <div class="split-line" />
      <div class="summary-item">
        <div class="text">
          Subtotal
        </div>
        <div class="count">
          <template v-if="isEmpty">
            --
          </template>
          <template v-else>
            <IntlCurrency
              :count="isLoading ? '0.00' : totalPriceWithDiscount.toFixed(2)"
              :size="14"
              color="#052784"
              :currency="currencyData?.targetCurrency.slice(0, 2)"
            />
          </template>
        </div>
      </div>
      <div
        v-if="!isEmpty"
        class="checkout-warpper"
      >
        <div
          v-if="isLoading || !checkedList.length"
          class="checkout-button disabled"
        >
          Checkout
        </div>
        <template v-else>
          <div
            v-if="allowOrderSwitch !== 1 && !inlcudeProcessing"
            class="tip-text"
          >
            <img
              src="@/assets/status/warning-blue.svg"
              alt=""
            >
            <div class="text">
              We are a platform dedicated to B2B procurement. Please submit your application to enable the order function.
            </div>
          </div>
          <div
            @click="handleCheckout"
            class="checkout-button"
            :class="{ disabled: disabledCheckout }"
          >
            <template v-if="allowOrderSwitch === 1 || inlcudeProcessing">
              Checkout
            </template>
            <template v-else>
              <img
                src="@/assets/H5/carts/whatsapp-icon.svg"
                width="24px"
                alt=""
              >
              Enable Order Function
            </template>
          </div>
        </template>
      </div>
      <CouponApply
        v-if="!coupon"
        class="coupon-apply"
      />
    </div>
    <div class="secure-payments">
      <div class="header">
        <img
          src="@/assets/order/secure-payments.svg"
          alt=""
        >
        Secure Payments
      </div>
      <div class="payment-methods">
        Payment methods:
      </div>
      <img
        class="pay-type"
        src="@/assets/offer/pay-type.png"
        height="20px"
        alt=""
      >
    </div>
    <div class="help-text">
      <div class="text">
        Each payment you make on Bestdealer is secured with strict SSL encryption and PCI DSS data protection protocols.
      </div>
      <img
        src="@/assets/order/secure-payments-help.png"
        width="80px"
        alt=""
      >
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { bankersRound, gaAnalytics } from '@/utils/utils'
import { fetchData } from '@/utils/fetch'
import { usePlugin } from '@/hooks/usePlugin'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { buriedPointUtil } from '../../../../../utils/utils'
import CouponDetail from '../../CouponDetail/index.vue'
import { useCoupon } from '@/views/Carts/hooks/useCoupon'
import { useCurrency } from '@/hooks/useCurrency'
import { useIntercom } from '@/hooks/useIntercom'
import CouponApply from '../../CouponApply/index.vue'

const props = defineProps({
  checkedList: {
    type: Array,
    default: () => []
  },
  currencyData: {
    type: Object,
    default: () => {}
  },
  isEmpty: {
    type: Boolean,
    default: false
  },
  isLoading: Boolean,
  allowOrderSwitch: {
    tyep: Number,
    default: 0
  }
})

const emits = defineEmits(['showProcessDialog'])

const store = useStore()
const router = useRouter()

const { $toast } = usePlugin()

const { initCoupon, coupon } = useCoupon()
const { exchangeCurrency } = useCurrency()
const intercom = useIntercom()

const isFixed = ref(false)

const handleScroll = (event) => {
  if (window.scrollY > 221) {
    isFixed.value = true
  } else {
    isFixed.value = false
  }
}

const totalPrice = computed(() => {
  const list = props.checkedList
  const currencyData = props.currencyData
  let total = 0
  list.forEach(item => {
    total += bankersRound(currencyData?.rate * item?.skuPriceRmb, 2) * item.skuQuantity
  })
  return total
})
const isCouponAvailable = computed(() => {
  if (coupon.value == null || props.currencyData == null) return false
  const minOrderAmount = exchangeCurrency(coupon.value.minOrderAmount, 'USD', props.currencyData.targetCurrency)
  return totalPrice.value >= minOrderAmount
})
const totalPriceWithDiscount = computed(() => {
  let total = totalPrice.value
  if (isCouponAvailable.value) {
    const targetDiscountAmount = exchangeCurrency(coupon.value.discountAmount, 'USD', props.currencyData?.targetCurrency)
    total -= targetDiscountAmount
  }
  return total
})
const getPrice = computed(() => {
  const list = props.checkedList
  const currencyData = props.currencyData
  let totalItems = 0
  let subTotal = 0
  list.forEach(item => {
    totalItems += bankersRound(currencyData?.rate * item?.skuPriceRmb, 2) * item.skuQuantity
    subTotal += bankersRound(currencyData?.rate * item?.skuPriceRmb, 2) * item.skuQuantity
  })
  return {
    totalItems: totalItems.toFixed(2),
    subTotal: subTotal.toFixed(2)
  }
})

const disabledCheckout = computed(() => {
  const list = props.checkedList
  const hasProcessing = list.filter(item => item.cartStatus === 0).length > 0
  const hasUnavailabel = list.filter(item => isUnavailable(item.cartStatus)).length > 0
  return hasProcessing || hasUnavailabel
})

const ceilingVisible = computed(() => store.state.ceilingVisible)

const calcFixedTop = computed(() => {
  if (ceilingVisible.value) {
    return '132px'
  } else {
    return '24px'
  }
})

const inlcudeProcessing = computed(() => {
  const len = props.checkedList.filter(item => item.cartStatus === 0).length
  return len > 0
})

const isUnavailable = (status) => {
  return [50, 51, 52].includes(Number(status))
}

const handleCheckout = async () => {
  const list = props.checkedList
  const hasProcessing = list.filter(item => item.cartStatus === 0).length > 0
  const hasUnavailabel = list.filter(item => isUnavailable(item.cartStatus)).length > 0

  if (hasProcessing) {
    emits('showProcessDialog')
    return
  }
  if (hasUnavailabel) return

  if (props.checkedList.length === 0) {
    return $toast('Please select at least one product', { type: 'error' })
  }

  gaAnalytics('p_checkout_click', 'p_checkout_click')
  buriedPointUtil({ eventId: 'PC203' })

  // 检查每个SPU是否满足最小批量
  const spuIds = [...new Set(props.checkedList.map((item) => item.spuId))]
  for (const spuId of spuIds) {
    const batchNum = getSpuBatchNum(spuId)
    const quantity = getSpuQuantity(spuId)
    if (quantity < batchNum) {
      $toast('There are products for which the minimum order quantity has not been reached.', { type: 'error' })
      return
    }
  }

  // 处理 Enable Order Function
  if (props.allowOrderSwitch === 0) {
    intercom.showNewMessage()
    return
  }

  sessionStorage.setItem('carts', JSON.stringify(props.checkedList.map(item => item.cartId)))
  router.push('/orderFilling')
}

const getSpuBatchNum = (spuId) => {
  const spuItems = props.checkedList.filter((item) => item.spuId === spuId)
  return Math.max(...spuItems.map((item) => item.batchNum))
}

const getSpuQuantity = (spuId) => {
  const spuItems = props.checkedList.filter((item) => item.spuId === spuId)
  return spuItems.reduce((sum, item) => sum + Number(item.skuQuantity), 0)
}

onBeforeMount(() => {
  initCoupon()
})

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

</script>

<style lang='less' scoped>
.right-fixed-info {
  transition: all .3s ease;
  &.fixed {
    position: fixed;
    top: 24px;
    right: calc((100vw - 1200px) / 2);
  }
  .coupon-detail {
    margin-bottom: 8px;
    width: 286px;
  }

  .coupon-apply {
    margin-top: 12px;
  }

  .summary-info {
    width: 286px;
    border-radius: 8px;
    background-color: #f8f8f8;
    padding: 16px;
    .summary-info-title {
      color: #232323;
      font-size: 14px;
      font-family: mon-700;
      margin-bottom: 16px;
    }
    .summary-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #232323;
      font-size: 12px;
      font-family: mon-600;
      margin-bottom: 12px;
      &:last-child {
        margin-bottom: 0;
      }

      .count {
        display: flex;
        align-items: center;
        font-size: 12px;
        font-family: mon-600;
        color: #232323;
      }
    }
    .split-line {
      border: 0.5px solid var(--dark-split-color);
      margin-bottom: 16px;
    }
    .checkout-warpper {
      margin-top: 16px;
      .tip-text {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        width: 254px;
        border-radius: 4px;
        border: 1px solid #DFE8FB;
        padding: 8px 12px;
        background-color: #EFF4FD;
        .text {
          color: #1153d6;
          font-size: 11px;
          font-family: mon-500;
          line-height: 16px;
        }
      }
      .checkout-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 254px;
        height: 36px;
        border-radius: 20px;
        background-color: #052784;
        color: white;
        font-size: 14px;
        font-family: mon-600;
        margin-top: 16px;
        cursor: pointer;
        img {
          margin-right: 4px;
        }

        &.disabled {
          opacity: .6;
        }
      }
    }
  }
  .secure-payments {
    margin-top: 16px;
    .header {
      display: flex;
      align-items: center;
      color: #232323;
      font-size: 14px;
      font-family: mon-600;
      img {
        margin-right: 8px;
      }
    }
    .payment-methods {
      margin-left: 32px;
      margin-top: 4px;
      margin-bottom: 2px;
      color: #5c5c5c;
      font-size: 12px;
      font-family: mon-500;
      line-height: 16px;
    }
    .pay-type {
      margin-left: 32px;
    }
  }
  .help-text {
    display: flex;
    align-items: center;
    margin-top: 12px;
    padding: 16px 12px;
    width: 286px;
    border: 1px solid var(--main-border-color);
    border-radius: 6px;
    .text {
      color: #5c5c5c;
      font-size: 11px;
      font-family: mon-500;
      line-height: 16px;
    }
  }
}
</style>
